import moment from 'moment';
import {
  DaypartException,
  LegacyRide,
  Ride,
  RideLocation,
  UserInRide,
  Particularity,
} from '../../interfaces';
import { collection, deleteField, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../app.component';
import { valueToDate } from '../../globals';
import { PlanType } from '../../enums';

export function getRideTitle(ride: LegacyRide) {
  let title = '';

  if (ride.isRetour) {
    title = `Retour `;
  }

  if (!ride.automaticallyPlanned) {
    // If the ride is not automatically planned, we can use the availableSeats to determine the title
    title = ride.availableSeats === 0 ? `${title}VOL` : `${title}${ride.availableSeats} Vrij`;
  }

  if (ride.comments) {
    title = `${title}, ${ride.comments}`;
  }

  if (!ride.users) {
    ride.users = {} as Map<string, any>;
  }
  const userIds = Object.keys(ride.users);
  const users = [];
  userIds.forEach((userId) => {
    const user = ride.users[userId];
    if (user.title) {
      users.push(user);
    }
  });
  users.forEach((user) => {
    title = `${title}, ${user.title}`;
  });
  if (users.length > 0) {
    if (ride.rideVersion === 2 && ride.locationsOrder.length >= 2) {
      title = `${title}, van ${ride.locationsOrder[0].fullLocation}, naar ${ride.locationsOrder[ride.locationsOrder.length - 1].fullLocation}`;
    } else {
      title = `${title}, van ${ride.fromLocation}, naar ${ride.toLocation}`;
    }
  }

  if (title.startsWith(', ')) {
    title = title.substring(2);
  }

  return title;
}

export async function checkUserDoubleBooking(
  user: UserInRide,
  rideDates: { start: moment.Moment; end: moment.Moment },
  rideId: string,
  organisationId: string,
  excludeRideId?: string,
) {
  const rideStart = rideDates.start;
  const rideEnd = rideDates.end;
  const day = rideStart.date();
  const month = rideStart.month();
  const year = rideStart.year();
  const q = query(
    collection(db, `organisations/${organisationId}/rides`),
    where('userIds', 'array-contains', user.id),
    where('day', '==', day),
    where('month', '==', month),
    where('year', '==', year),
  );
  const querySnapshot = await getDocs(q);
  for (const rideDoc of querySnapshot.docs) {
    const ride = rideDoc.data();
    if ((rideId && rideId === rideDoc.id) || excludeRideId === rideDoc.id) {
      continue;
    }
    if (ride.users && ride.users[user.id]) {
      if (ride.locationsOrder && user.userDropoffTime && user.userPickupTime) {
        const userLocations = ride.locationsOrder.filter((loc: any) => {
          return loc.userId === user.id;
        });
        if (ride.rideVersion === 2) {
          const existingStart = moment(valueToDate(userLocations[0].time));
          const existingEnd = moment(valueToDate(userLocations[1].time));
          const userStart = moment(valueToDate(user.userPickupTime));
          const userEnd = moment(valueToDate(user.userDropoffTime));
          if (
            userStart.isBetween(existingStart, existingEnd, null, '()') ||
            userEnd.isBetween(existingStart, existingEnd, null, '()') ||
            existingStart.isBetween(userStart, userEnd, null, '()') ||
            existingEnd.isBetween(userStart, userEnd, null, '()')
          ) {
            return true;
          }
        }
      } else {
        const existingStart = moment(valueToDate(ride.start));
        const existingEnd = moment(valueToDate(ride.end));
        if (
          rideStart.isBetween(existingStart, existingEnd, null, '()') ||
          rideEnd.isBetween(existingStart, existingEnd, null, '()') ||
          existingStart.isBetween(rideStart, rideEnd, null, '()') ||
          existingEnd.isBetween(rideStart, rideEnd, null, '()')
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

export async function checkUserDoubleBookingAutomaticRides(
  user: UserInRide,
  rideDates: { start: moment.Moment; end: moment.Moment },
  rideId: string,
  organisationId: string,
  excludeRideId?: string,
) {
  const rideStart = rideDates.start;
  const rideEnd = rideDates.end;
  const startOfDay = moment(valueToDate(rideStart)).startOf(`day`).toDate();
  const endOfDay = moment(valueToDate(rideStart)).endOf(`day`).toDate();
  const q = query(
    collection(db, `organisations/${organisationId}/automaticRides`),
    where('passenger.id', '==', user.id),
    where(`start`, `>=`, startOfDay),
    where(`start`, `<=`, endOfDay),
    where('planningCompleted', '==', false),
  );
  const querySnapshot = await getDocs(q);
  for (const rideDoc of querySnapshot.docs) {
    const ride = rideDoc.data();
    if ((rideId && rideId === rideDoc.id) || excludeRideId === rideDoc.id) {
      continue;
    }
    const existingStart = moment(valueToDate(ride.start));
    const existingEnd = moment(valueToDate(ride.end));
    if (
      rideStart.isBetween(existingStart, existingEnd, null, '()') ||
      rideEnd.isBetween(existingStart, existingEnd, null, '()') ||
      existingStart.isBetween(rideStart, rideEnd, null, '()') ||
      existingEnd.isBetween(rideStart, rideEnd, null, '()')
    ) {
      return true;
    }
  }
  return false;
}

export function getExceptionsForDay(start: any, end: any, exceptions: DaypartException[]) {
  const currentWeek = moment(start).week();
  const currentMonth = moment(start).month();
  const currentYear = moment(start).year();
  const compareDate = moment(start);
  for (const exception of exceptions) {
    const exceptionDate = moment(valueToDate(exception.date));
    if (exception.repeat) {
      switch (exception.repeatValue) {
        case 'week':
          exceptionDate.year(currentYear);
          exceptionDate.month(currentMonth);
          exceptionDate.week(currentWeek);
          exceptionDate.day(moment(start).day());
          break;
        case 'month':
          exceptionDate.year(currentYear);
          exceptionDate.month(currentMonth);
          exceptionDate.day(moment(start).day());
          break;
        case 'year':
          exceptionDate.year(currentYear);
          break;
      }
    }
    if (exceptionDate.format('YYYY-MM-DD') !== compareDate.format('YYYY-MM-DD')) {
      continue;
    }
    if (
      exception.repeatValue &&
      (exception.repeatValue === 'week' || exception.repeatValue === 'month') &&
      moment(valueToDate(exception.date)).weekday() !== compareDate.weekday()
    ) {
      continue;
    }
    if (exception.allDay) {
      return exception;
    }

    const exceptionStart = moment(exceptionDate)
      .hours(exception.startHour)
      .minutes(exception.startMinutes);
    const exceptionEnd = moment(exceptionDate)
      .hours(exception.endHour)
      .minutes(exception.endMinutes);
    if (
      moment(start).isBetween(exceptionStart, exceptionEnd) ||
      moment(end).isBetween(exceptionStart, exceptionEnd)
    ) {
      return exception;
    }
  }
  return null;
}

export function updateRideToV2(ride: Ride) {
  if (ride.rideVersion === 2) {
    return ride;
  } else {
    const users = ride.users
      ? Object.values(ride.users).sort((x: any, y: any) => {
          return x.order - y.order;
        })
      : [];
    const updatedUsers = {};
    for (const user of users) {
      user.userPickupTime = valueToDate(user.userPickupTime ?? ride.start);
      user.userDropoffTime = valueToDate(user.userDropoffTime ?? ride.end);
      user.particularityIds = user?.particularityIds ?? [];
      if (ride.users[user.id]['wheelchair'] && !user.particularityIds.includes('wheelchair')) {
        user.particularityIds.push('wheelchair');
      }
      if (ride.users[user.id]['companion'] && !user.particularityIds.includes('companion')) {
        user.particularityIds.push('companion');
      }
      if (ride.users[user.id]['buslift'] && !user.particularityIds.includes('buslift')) {
        user.particularityIds.push('buslift');
      }
      if (ride.users[user.id]['cane'] && !user.particularityIds.includes('cane')) {
        user.particularityIds.push('cane');
      }
      if (ride.users[user.id]['childSeat'] && !user.particularityIds.includes('childseat')) {
        user.particularityIds.push('childseat');
      }
      if (ride.users[user.id]['escort'] && !user.particularityIds.includes('escort')) {
        user.particularityIds.push('escort');
      }
      if (ride.users[user.id]['guideDog'] && !user.particularityIds.includes('guidedog')) {
        user.particularityIds.push('guidedog');
      }
      if (ride.users[user.id]['rollator'] && !user.particularityIds.includes('walker')) {
        user.particularityIds.push('walker');
      }
      updatedUsers[user.id] = user;
    }
    ride.users = updatedUsers as Map<string, any>;
    ride.locationsOrder = buildLocationsOrder(ride);
    ride.rideVersion = 2;
    if (ride.vehicleRef && typeof ride.vehicleRef !== 'string') {
      ride.vehicleRef = typeof ride.vehicleRef.path === 'string' ? ride.vehicleRef.path : null;
    }
    return ride;
  }
}

export function createRetourRide(
  ride: any,
  organisationId: string,
  planType: string,
  rideDuration: number,
  minutesAfterParent: number = 15,
) {
  const collectionName = planType === PlanType.Automatic ? 'automaticRides' : 'rides';
  const retourId = doc(collection(db, `organisations/${organisationId}/${collectionName}`)).id;
  const retourRide = {} as any;

  const rideEnd = valueToDate(ride.end);
  const start = new Date(rideEnd).setMinutes(rideEnd.getMinutes() + minutesAfterParent);
  const end = new Date(rideEnd).setMinutes(
    rideEnd.getMinutes() + minutesAfterParent + rideDuration,
  );
  retourRide.comments = ride.comments;
  retourRide.start = start;
  retourRide.end = end;
  retourRide.isRetour = true;
  retourRide.id = retourId;
  retourRide.characteristics = ride.characteristics;
  retourRide.special = ride.special;

  if (planType === PlanType.Automatic) {
    retourRide.toLatitude = ride.fromLatitude;
    retourRide.toLongitude = ride.fromLongitude;
    retourRide.fromLatitude = ride.toLatitude;
    retourRide.fromLongitude = ride.toLongitude;
    retourRide.useBuffers = ride.useBuffers;

    retourRide.passenger = {
      ...ride.passenger,
      from: ride.passenger.to,
      fromType: ride.passenger.toType,
      to: ride.passenger.from,
      toType: ride.passenger.fromType,
      userDropoffTime: ride.passenger.userPickupTime,
      userPickupTime: ride.passenger.userDropoffTime,
    };
  } else {
    retourRide.availableSeats = ride.availableSeats;
    retourRide.rideProduct = ride.rideProduct;
    retourRide.rideVersion = 2;
    retourRide.vehicleId = ride.vehicleId;
    retourRide.vehicleRef = ride.vehicleRef;
    retourRide.title = getRideTitle(retourRide);
  }

  const users = Object.values(ride.users).sort((x: any, y: any) => {
    return x.order - y.order;
  });
  const retourUserObj = {};
  let retourUsers = [];
  users.forEach((val) => retourUsers.push(Object.assign({}, val)));

  retourUsers = retourUsers.reverse();
  for (const user of retourUsers) {
    if (!user) {
      return;
    }
    if (!user.rideProduct) {
      delete user.rideProduct;
      user.rideProduct = deleteField();
    }

    const newFrom = user.to;
    const newTo = user.from;
    const newFromType = user.toType;
    const newToType = user.fromType;
    user.from = newFrom;
    user.fromType = newFromType;
    user.to = newTo;
    user.toType = newToType;
    user.userPickupTime = moment(retourRide.start)
      .add(moment(valueToDate(user.userPickupTime)).diff(moment(ride.start), 'minutes'), 'minutes')
      .toDate();
    user.userDropoffTime = moment(retourRide.start)
      .add(moment(valueToDate(user.userDropoffTime)).diff(moment(ride.start), 'minutes'), 'minutes')
      .toDate();
    retourUserObj[user.id] = user;

    retourRide.users = retourUserObj as Map<string, any>;
    retourRide.locationsOrder = buildLocationsOrder(retourRide);
  }

  Object.keys(retourRide).forEach((key) => {
    if (retourRide[key] == null) {
      delete retourRide[key];
    }
  });

  return retourRide;
}

export function buildLocationsOrder(ride: Ride): RideLocation[] {
  const unorderedLocations: RideLocation[] = [];
  for (const user of Object.values(ride.users) as UserInRide[]) {
    // This is checked to make sure that deleted users are not included in the locations order. This has to be done this way as users is a map, not an array, so merge operations do not remove deleted users without this method.
    if (!user['_methodName'] || user['_methodName'] !== 'deleteField') {
      const locationPoints = [
        { type: 'from', time: valueToDate(user.userPickupTime) },
        { type: 'to', time: valueToDate(user.userDropoffTime) },
      ];
      for (const data of locationPoints) {
        const location: RideLocation = {
          userId: user.id,
          type: data.type,
          time: data.time,
          location: user[data.type],
          fullLocation: getFullLocationString(user[data.type]),
        };
        unorderedLocations.push(location);
      }
    }
  }
  const timeSlots = getTimeSlots(unorderedLocations);
  let locationsOrder = [];
  timeSlots.forEach((timeSlot) => {
    locationsOrder = locationsOrder.concat(timeSlot.locations);
  });
  return locationsOrder;
}

export function getCopiedLocationsOrder(sourceRide: Ride, targetRide: Ride): RideLocation[] {
  if (!targetRide.users) return [];

  const targetLocations = buildLocationsOrder(targetRide);

  if (!sourceRide.locationsOrder) {
    return targetLocations;
  }

  const targetLocationsByKey = new Map(
    targetLocations.map((location) => [`${location.userId}:${location.type}`, location]),
  );

  return sourceRide.locationsOrder
    .map((location) => targetLocationsByKey.get(`${location.userId}:${location.type}`))
    .filter((location): location is RideLocation => !!location);
}

export function getTimeSlots(locationsOrder: RideLocation[]) {
  const timeSlots = [];
  for (const location of locationsOrder) {
    location.time = valueToDate(location.time);
    const timeSlotExists = timeSlots.find((timeSlot) => {
      return moment(timeSlot.time).isSame(moment(location.time));
    });
    if (timeSlotExists) {
      timeSlotExists.locations.push(location);
    } else {
      timeSlots.push({ time: location.time, locations: [location] });
    }
  }

  timeSlots.sort((a, b) => {
    return moment(a.time).diff(moment(b.time));
  });
  return timeSlots;
}

export interface AutomaticRideOccupancy {
  time: Date;
  pickupCount: number;
  dropoffCount: number;
  passengersOnboard: number;
  availableSeats: number;
}

export function calculateAutomaticRideOccupancy(
  locationsOrder: RideLocation[],
  vehicleCapacity: number,
  coDriverId?: string | null,
  users: UserInRide[] = [],
  particularities: Particularity[] = [],
): AutomaticRideOccupancy[] {
  const timeSlots = getTimeSlots(
    locationsOrder.map((location) => ({ ...location, time: valueToDate(location.time) })),
  );
  let passengersOnboard = coDriverId ? 1 : 0;

  const seatsForUser = (userId: string) => {
    const user = users.find((candidate) => candidate.id === userId);
    const additionalSeats = particularities.filter(
      (particularity) =>
        particularity.takesSeat && user?.particularityIds?.includes(particularity.id),
    ).length;
    return 1 + additionalSeats;
  };

  return timeSlots.map((timeSlot) => {
    const dropoffLocations = timeSlot.locations.filter((location) => location.type === 'to');
    const pickupLocations = timeSlot.locations.filter((location) => location.type === 'from');
    const dropoffSeats = dropoffLocations.reduce(
      (seats, location) => seats + seatsForUser(location.userId),
      0,
    );
    const pickupSeats = pickupLocations.reduce(
      (seats, location) => seats + seatsForUser(location.userId),
      0,
    );
    passengersOnboard = passengersOnboard - dropoffSeats + pickupSeats;

    return {
      time: valueToDate(timeSlot.time),
      pickupCount: pickupSeats,
      dropoffCount: dropoffSeats,
      passengersOnboard,
      availableSeats: vehicleCapacity - passengersOnboard,
    };
  });
}

export function onMapsClick(event: Event, ride: Ride) {
  event.stopPropagation();
  const mapsUrl = getMapsRouteUrl(ride);
  if (ride && mapsUrl) {
    window.open(mapsUrl, '_blank');
  }
}

export function getMapsRouteUrl(ride: Ride) {
  if (!ride.locationsOrder || ride.locationsOrder.length === 0) return;

  const locations = [...ride.locationsOrder]
    .sort((a, b) => a.time - b.time) // Sort by time to ensure correct order
    .map((loc) => {
      // Handle both string and object location formats
      return getMapsLocationString(loc.location ?? loc.fullLocation);
    });

  if (locations.length === 0) return;

  let mapsUrl: string;

  // create directions
  const origin = locations[0];
  const destination = locations[locations.length - 1];
  const waypoints = locations.slice(1, -1);

  mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
  if (waypoints.length > 0) {
    mapsUrl += `&waypoints=${waypoints.join('|')}`;
  }
  return mapsUrl;
}

function getMapsLocationString(location: any) {
  if (!location) return '';
  return typeof location === 'string'
    ? location
    : (location.address ?? getFullLocationString(location));
}

export function getWeekString(date: any) {
  const weekYear = moment(date).isoWeekYear();
  const week = moment(date).isoWeek();
  return `${weekYear}-${week}`;
}

export function getFullLocationString(location: any) {
  if (!location) return '';
  return typeof location === 'string' ? location : `${location.name} - ${location.address}`;
}

export function getUserById(users: UserInRide[], id: string) {
  return users.find((user) => user.id === id);
}

export function calculateOverlapPercentage(
  startDate: Date,
  endDate: Date,
  rideStart: Date,
  rideEnd: Date,
): number {
  const currentStart = startDate.getTime();
  const currentEnd = endDate.getTime();
  const otherStart = rideStart.getTime();
  const otherEnd = rideEnd.getTime();

  const overlapStart = Math.max(currentStart, otherStart);
  const overlapEnd = Math.min(currentEnd, otherEnd);

  if (overlapStart >= overlapEnd) {
    return 0;
  }

  const overlapDuration = overlapEnd - overlapStart;
  const currentRideDuration = currentEnd - currentStart;

  return overlapDuration / currentRideDuration;
}

export function calculateSeatsTaken(ride: any, particularities: Particularity[]) {
  let requiredSeats = 1;
  const rideData = ride.passenger ? ride.passenger : ride;
  for (const particularity of particularities) {
    if (rideData?.particularityIds?.includes(particularity.id) && particularity.takesSeat) {
      requiredSeats++;
    }
  }
  return requiredSeats;
}

export function cleanAddressForApi(address: string): string {
  if (!address || typeof address !== 'string') {
    return '';
  }

  let cleanedAddress = address.trim();
  cleanedAddress = cleanedAddress.replace(/[™®©]/g, '');
  cleanedAddress = cleanedAddress.replace(/(\d{4})\s+([A-Za-z]{2})/g, '$1$2');

  cleanedAddress = cleanedAddress
    .replace(/\s+/g, ' ')
    .replace(/,\s*,/g, ',')
    .replace(/,\s*$/, '')
    .trim();

  return cleanedAddress;
}

export async function getRideCopyVehicleError(
  organisationId: string,
  ride: Partial<Ride>,
): Promise<string | null> {
  if (!ride.vehicleId) {
    return 'missingVehicle';
  }

  const vehicleSnapshot = await getDoc(
    doc(db, `organisations/${organisationId}/vehicles/${ride.vehicleId}`),
  );
  if (!vehicleSnapshot.exists()) {
    return 'vehicleNotFound';
  }
  if (vehicleSnapshot.data().active !== true) {
    return 'vehicleInactive';
  }

  return null;
}
